import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  LogOut, 
  Settings, 
  Plus, 
  Archive, 
  Calendar, 
  Clock,
  Trash, 
  ChevronLeft, 
  ChevronRight,
  AlertCircle,
  Search,
  FileText,
  Filter,
  X,
  Check
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "@/components/ui/tooltip";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { cn, colors, typography, spacing, effects, componentStyles } from '../../designSystem';

const Sidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  isTabletView,
  username,
  handleLogout,
  setIsSettingsOpen,
  konsultasjoner,
  currentKonsultasjon,
  setCurrentKonsultasjon,
  startNewConsultation,
  handleDeleteKonsultasjon,
  handleDeleteAllKonsultasjoner,
  DEFAULT_TEMPLATE,
  isRecording = false
}) => {
  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'ongoing', 'completed'
  const [filteredKonsultasjoner, setFilteredKonsultasjoner] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  
  // Sidebar width - wider open sidebar
  const sidebarWidth = isSidebarOpen ? "w-80" : "w-16";
  
  // Calculate count of ongoing and completed consultations
  const ongoingCount = konsultasjoner.filter(k => 
    !(k.status === 'completed' || k.status === 'fullført' || k.completed === true)
  ).length;
  
  const completedCount = konsultasjoner.filter(k => 
    k.status === 'completed' || k.status === 'fullført' || k.completed === true)
  .length;

  // Filter consultations based on search and status
  useEffect(() => {
    let filtered = [...konsultasjoner];
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(k => {
        const isCompleted = k.status === 'completed' || 
                          k.status === 'fullført' || 
                          k.completed === true;
        return statusFilter === 'completed' ? isCompleted : !isCompleted;
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(k => {
        // Try multiple possible field names for transcription
        const transcriptionMatch = 
          (k.transkripsjon && k.transkripsjon.toLowerCase().includes(query)) ||
          (k.transcription && k.transcription.toLowerCase().includes(query)) || 
          (k.transcript && k.transcript.toLowerCase().includes(query));
        
        return (k.title || "").toLowerCase().includes(query) ||
               (k.notat || "").toLowerCase().includes(query) ||
               (k.kontekst || "").toLowerCase().includes(query) ||
               transcriptionMatch;
      });
    }
    
    // Sort by newest first
    filtered = filtered.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
    
    setFilteredKonsultasjoner(filtered);
  }, [konsultasjoner, searchQuery, statusFilter]);
  
  // Format relative date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    
    if (diffMin < 60) {
      return `${diffMin} min siden`;
    } else if (diffHrs < 24) {
      return `${diffHrs} timer siden`;
    } else {
      return date.toLocaleDateString();
    }
  };
  
  // Get status information for a consultation
  const getConsultationStatus = (konsultasjon) => {
    const isActiveRecording = isRecording && currentKonsultasjon?.id === konsultasjon.id;
    const isCompleted = konsultasjon.status === 'completed' || 
                       konsultasjon.status === 'fullført' || 
                       konsultasjon.completed === true;
    const hasNote = konsultasjon.notat && konsultasjon.notat.trim() !== '';
    
    if (isCompleted || hasNote) {
      return {
        text: "Fullført",
        statusClass: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        borderClass: "border-green-200 dark:border-green-800/50",
        hoverBgClass: "hover:bg-green-50/50 dark:hover:bg-green-900/10",
        activeBgClass: "bg-green-50/70 dark:bg-green-900/20",
        gradientClass: "from-emerald-500 to-green-600"
      };
    } else if (isActiveRecording) {
      return {
        text: "Opptak",
        statusClass: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400",
        borderClass: "border-red-200 dark:border-red-800/50",
        hoverBgClass: "hover:bg-red-50/50 dark:hover:bg-red-900/10",
        activeBgClass: "bg-red-50/70 dark:bg-red-900/20",
        gradientClass: "from-red-500 to-rose-600"
      };
    } else {
      return {
        text: "Påbegynt",
        statusClass: "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
        borderClass: "border-blue-200 dark:border-blue-800/50",
        hoverBgClass: "hover:bg-blue-50/50 dark:hover:bg-blue-900/10",
        activeBgClass: "bg-blue-50/70 dark:bg-blue-900/20",
        gradientClass: "from-blue-500 to-indigo-600"
      };
    }
  };
  
  // Toggle search UI
  const toggleSearch = () => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true);
      setTimeout(() => setShowSearch(true), 300);
    } else {
      setShowSearch(prev => !prev);
      if (!showSearch) {
        document.getElementById('search-input')?.focus();
      }
    }
  };

  return (
    <aside className={cn(
      "h-screen bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800",
      "transition-all duration-300 ease-in-out relative",
      "flex flex-col",
      sidebarWidth
    )}>
{/* Interactive edge - usynlig klikkeområde */}
<div 
  className={cn(
    "absolute top-0 bottom-0 right-0",
    "w-[24.5px]", 
    "cursor-pointer",
    "z-10"
  )}
  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
/>

{/* Pull tab - synlig del */}
<div 
  className={cn(
    "absolute top-1/2 -translate-y-1/2",
    isSidebarOpen ? "-right-[26px]" : "-right-[26px]",
    "h-64 w-[26px]", // Lengre og bredere for bedre synlighet
    "bg-transparent dark:bg-transparent",
    "border-transparent dark:border-transparent",
    "rounded-r-lg",
  
    
    "flex flex-col items-center justify-center",
    "cursor-pointer",
    "hover:bg-blue-100 hover:bg-opacity-80 dark:hover:bg-neutral-600",
    "transition-all duration-200",
    "z-30"
  )}
  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
>
   {/* Grå pil-ikon */}
   {isSidebarOpen ? (
    <ChevronLeft className="h-4 w-4 text-gray-400 dark:text-neutral-500" />
  ) : (
    <ChevronRight className="h-4 w-4 text-gray-400 dark:text-neutral-500" />
  )}

</div>
  
      {/* Header Section */}
      <div className="p-4 flex items-center justify-between">
        {/* App Logo & Title */}
        <div className={cn(
          "flex items-center",
          isSidebarOpen ? "gap-3" : ""
        )}>
          <div className={cn(
            "flex-shrink-0 w-9 h-9 rounded-xl",
            "bg-gradient-to-r from-blue-600 to-indigo-600",
            "flex items-center justify-center",
            "relative overflow-hidden",
            "shadow-sm"
          )}>
            {/* Add subtle radial gradient overlay */}
            <div className="absolute inset-0 bg-white/10 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.2)_0%,_transparent_70%)]" />
            <Brain className="h-5 w-5 text-white relative z-10" />
          </div>
          
          {isSidebarOpen && (
            <div>
              <h2 className={cn(
                "text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400",
                "text-xl font-semibold"
              )}>
                Skribo
              </h2>
              <div className="text-xs font-medium italic text-blue-600/80 dark:text-blue-400/60">
Journalføring gjort enkelt              </div>
            </div>
          )}
        </div>
        
        {/* Search & Toggle Button */}
        {isSidebarOpen && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={toggleSearch}
          >
            <Search className="h-4 w-4 text-gray-500 dark:text-neutral-400" />
          </Button>
        )}
      </div>
      
      {/* Search Bar (conditional) */}
      {isSidebarOpen && showSearch && (
        <div className="px-4 pb-3 animate-in fade-in slide-in-from-top duration-300">
          <div className="relative">
            <Input
              id="search-input"
              type="text"
              placeholder="Søk i konsultasjoner..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                "pl-9 pr-8 h-9 text-sm",
                "border border-gray-200 dark:border-neutral-700",
                "bg-white dark:bg-neutral-800",
                "placeholder:text-gray-400 dark:placeholder:text-neutral-500"
              )}
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5"
              >
                <X className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" />
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* User Profile Section */}
      {isSidebarOpen && (
        <div className={cn(
          "mx-4 mb-4 p-3 rounded-lg",
          "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10",
          "border border-blue-100 dark:border-blue-800/30"
        )}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 dark:text-neutral-400">
                Innlogget som
              </p>
              <p className="text-sm font-medium text-blue-700 dark:text-blue-300 truncate pr-2">
                {username || "Bruker"}
              </p>
            </div>
            
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full hover:bg-blue-100/50 dark:hover:bg-blue-900/20"
                      onClick={() => setIsSettingsOpen(true)}
                    >
                      <Settings className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Innstillinger</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost" 
                      size="icon"
                      className="h-7 w-7 rounded-full hover:bg-blue-100/50 dark:hover:bg-blue-900/20"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Logg ut</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      )}
      
      {/* Action Buttons */}
      <div className={cn(
        "mb-5",
        isSidebarOpen ? "px-4 space-y-2" : "px-3 flex flex-col items-center gap-3"
      )}>
        {/* New Consultation Button */}
        <Button
          onClick={startNewConsultation}
          className={cn(
            isSidebarOpen 
              ? "w-full h-9 text-white shadow-md" 
              : "w-10 h-10 p-0 flex items-center justify-center",
            "bg-gradient-to-r from-blue-600 to-indigo-600",
            "hover:from-blue-700 hover:to-indigo-700",
            "rounded-lg"
          )}
        >
          {isSidebarOpen ? (
            <div className="flex items-center">
              <Plus className="mr-2 h-4 w-4" />
              <span>Ny konsultasjon</span>
            </div>
          ) : (
            <Plus className="h-5 w-5 text-white" />
          )}
        </Button>
        
        {/* Archive Button */}
        {isSidebarOpen ? (
          <Button
            onClick={handleDeleteAllKonsultasjoner}
            variant="outline"
            className={cn(
              "w-full",
              "text-gray-600 dark:text-neutral-300",
              "hover:bg-gray-50 dark:hover:bg-neutral-800",
              "border-gray-200 dark:border-neutral-700"
            )}
          >
            <Archive className="mr-2 h-4 w-4 text-gray-500 dark:text-neutral-400" />
            <span>Arkiver ferdige</span>
          </Button>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleDeleteAllKonsultasjoner}
                  variant="outline" 
                  className="h-10 w-10 p-0"
                >
                  <Archive className="h-5 w-5 text-gray-500 dark:text-neutral-400" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Arkiver ferdige konsultasjoner</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      {/* Filter Tabs - Only visible when sidebar is open */}
      {isSidebarOpen && (
        <div className="px-4 mb-3">
          <div className="flex items-center space-x-1">
            <Button
              size="sm"
              variant={statusFilter === 'all' ? "default" : "ghost"}
              onClick={() => setStatusFilter('all')}
              className={cn(
                "h-8 px-2.5 text-xs",
                statusFilter === 'all' 
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                  : "text-gray-600 dark:text-neutral-300"
              )}
            >
              Alle
              <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-[10px]">
                {konsultasjoner.length}
              </span>
            </Button>
            
            <Button
              size="sm"
              variant={statusFilter === 'ongoing' ? "default" : "ghost"}
              onClick={() => setStatusFilter('ongoing')}
              className={cn(
                "h-8 px-2.5 text-xs",
                statusFilter === 'ongoing' 
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                  : "text-gray-600 dark:text-neutral-300"
              )}
            >
              Påbegynt
              <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-[10px]">
                {ongoingCount}
              </span>
            </Button>
            
            <Button
              size="sm"
              variant={statusFilter === 'completed' ? "default" : "ghost"}
              onClick={() => setStatusFilter('completed')}
              className={cn(
                "h-8 px-2.5 text-xs",
                statusFilter === 'completed' 
                  ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white"
                  : "text-gray-600 dark:text-neutral-300"
              )}
            >
              Fullført
              <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-[10px]">
                {completedCount}
              </span>
            </Button>
          </div>
        </div>
      )}

      {/* Main List Section */}
      {isSidebarOpen ? (
        <>
          <div className="px-4 flex items-center justify-between mb-2">
            <h3 className="text-xs font-medium text-gray-500 dark:text-neutral-400">
              Konsultasjoner
            </h3>
            
            {/* Sort/Filter dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <Filter className="h-3.5 w-3.5 text-gray-500 dark:text-neutral-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                  <div className="flex items-center w-full">
                    <span className="flex-1">Alle</span>
                    {statusFilter === 'all' && <Check className="h-4 w-4" />}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('ongoing')}>
                  <div className="flex items-center w-full">
                    <span className="flex-1">Pågående</span>
                    {statusFilter === 'ongoing' && <Check className="h-4 w-4" />}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('completed')}>
                  <div className="flex items-center w-full">
                    <span className="flex-1">Fullførte</span>
                    {statusFilter === 'completed' && <Check className="h-4 w-4" />}
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Separator */}
          <div className="px-4 mb-2">
            <Separator className="dark:bg-neutral-800" />
          </div>
          
          {/* Consultation List */}
          <ScrollArea className="px-4 h-[calc(100vh-325px)]">
            {filteredKonsultasjoner.length === 0 ? (
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center mb-3">
                  <FileText className="h-6 w-6 text-gray-400 dark:text-neutral-500" />
                </div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">
                  Ingen konsultasjoner
                </h4>
                <p className="text-xs text-gray-500 dark:text-neutral-500 max-w-[200px] mx-auto">
                  {searchQuery 
                    ? "Ingen resultater funnet. Prøv andre søkeord eller fjern filtre." 
                    : "Start en ny konsultasjon for å begynne."}
                </p>
                
                {searchQuery && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('all');
                    }}
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Nullstill søk
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2 pr-2">
                {filteredKonsultasjoner.map((k) => {
                  const status = getConsultationStatus(k);
                  const isActive = currentKonsultasjon?.id === k.id;
                  
                  return (
                    <div
                      key={k.id}
                      className={cn(
                        "group relative rounded-lg cursor-pointer overflow-hidden",
                        "border transition-all duration-200",
                        isActive 
                          ? cn(status.activeBgClass, status.borderClass) 
                          : cn("border-gray-200 dark:border-neutral-800", status.hoverBgClass),
                        isActive ? "shadow-sm" : "hover:shadow-sm",
                        "transform-gpu hover:translate-x-1 active:translate-x-0"
                      )}
                      onClick={() => setCurrentKonsultasjon(k)}
                    >
                      {/* The colored left border that stays visible all the time for active item */}
                      {isActive && (
                        <div 
                          className={cn(
                            "absolute left-0 top-0 bottom-0 w-1.5", 
                            `bg-gradient-to-b ${status.gradientClass}`
                          )} 
                        />
                      )}
                      
                      {/* This is the colored left border that appears on hover */}
                      <div 
                        className={cn(
                          "absolute left-0 top-0 bottom-0 w-1.5", 
                          `bg-gradient-to-b ${status.gradientClass}`,
                          // Hide on active items (since they already have a visible border)
                          isActive ? "opacity-0" : "opacity-0 group-hover:opacity-100",
                          "transition-opacity duration-200"
                        )} 
                      />
                      
                      <div className={cn(
                        "p-3",
                        isActive ? "pl-4" : "pl-3 group-hover:pl-4",
                        "transition-all duration-200"
                      )}>
                        <div className="flex items-start justify-between mb-1">
                          <h3 className="text-sm font-medium text-gray-800 dark:text-neutral-200 truncate pr-6">
                            {k.title || "Uten tittel"}
                          </h3>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteKonsultasjon(k);
                            }}
                            className={cn(
                              "opacity-0 group-hover:opacity-100 focus:opacity-100",
                              "transition-opacity absolute right-2 top-2.5",
                              "text-gray-400 hover:text-red-500 dark:text-neutral-500 dark:hover:text-red-400"
                            )}
                            title="Slett konsultasjon"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center text-xs text-gray-500 dark:text-neutral-500 mb-2">
                          <Calendar className="h-3 w-3 mr-1.5" />
                          <span>{new Date(k.start_time).toLocaleDateString()}</span>
                          <div className="mx-1.5 h-0.5 w-0.5 rounded-full bg-gray-300 dark:bg-neutral-600" />
                          <Clock className="h-3 w-3 mr-1.5" />
                          <span>{new Date(k.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className={cn(
                            "text-[10px] rounded-full px-2 py-0.5",
                            status.statusClass
                          )}>
                            {status.text}
                          </span>
                          
                          <div className="text-[10px] text-gray-400 dark:text-neutral-500">
                            {formatDate(k.start_time)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </>
      ) : (
        // Collapsed view
        <div className="flex-1 flex flex-col items-center pt-4">
          {/* Search toggle when collapsed */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost" 
                  size="icon"
                  className="h-9 w-9 rounded-lg mb-3 bg-gray-50 dark:bg-neutral-800"
                  onClick={toggleSearch}
                >
                  <Search className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Søk i konsultasjoner</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Collapsed consultation list */}
          {konsultasjoner.length > 0 ? (
            <ScrollArea className="w-full h-[calc(100vh-220px)]">
              <div className="flex flex-col items-center space-y-2 py-1">
                {konsultasjoner
                  .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
                  .slice(0, 8)
                  .map((k) => {
                    const status = getConsultationStatus(k);
                    const isActive = currentKonsultasjon?.id === k.id;
                    
                    return (
                      <TooltipProvider key={k.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                          <div className="group">
  <button
    className={cn(
      "w-10 h-10 rounded-lg flex items-center justify-center",
      "transition-all duration-300",
      "overflow-hidden",
      "relative",
      isActive 
        ? `bg-gradient-to-r ${status.gradientClass} text-white shadow-sm`
        : "bg-gray-50 dark:bg-neutral-800 text-gray-600 dark:text-neutral-400"
    )}
    onClick={() => setCurrentKonsultasjon(k)}
  >
    {/* Hover gradient overlay that fades in */}
    {!isActive && (
      <div 
        className={cn(
          "absolute inset-0 opacity-0 group-hover:opacity-100",
          `bg-gradient-to-r ${status.gradientClass}`,
          "transition-opacity duration-300 ease-in-out"
        )} 
      />
    )}
    
    <FileText 
      className={cn(
        "h-5 w-5 z-10 relative",
        !isActive && "group-hover:text-white transition-colors duration-300"
      )} 
    />
  </button>
</div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-[220px]">
                            <div className="text-sm font-medium mb-0.5">
                              {k.title || "Uten tittel"}
                            </div>
                            <div className="text-xs opacity-80 flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {new Date(k.start_time).toLocaleDateString()}
                            </div>
                            <div className="text-xs mt-1">
                              <span className={cn(
                                "px-1.5 py-0.5 rounded-full text-[10px]",
                                status.statusClass
                              )}>
                                {status.text}
                              </span>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                
                {/* Show count if there are more */}
                {konsultasjoner.length > 8 && (
                  <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-gray-600 dark:text-neutral-400 text-xs">
                        +{konsultasjoner.length - 8}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Totalt {konsultasjoner.length} konsultasjoner</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </ScrollArea>
        ) : (
          <div className="text-center py-6">
            <div className="rounded-lg bg-gray-100 dark:bg-neutral-800 p-3 mx-auto mb-2">
              <FileText className="h-5 w-5 text-gray-400 dark:text-neutral-500" />
            </div>
            <span className="text-[10px] text-gray-400 dark:text-neutral-500">
              Ingen
            </span>
          </div>
        )}
      </div>
    )}
    
    {/* Footer Section with Warning */}
    <div className="mt-auto">
      {isSidebarOpen ? (
        <div className="p-4 pt-0">
          <Separator className="mb-4 dark:bg-neutral-800" />
          <div className="text-xs text-center p-2.5 border rounded-lg bg-amber-50 dark:bg-neutral-800 border-amber-200 dark:border-neutral-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <span className="font-medium text-amber-600 dark:text-amber-500">
                Automatisk sletting
              </span>
            </div>
            <p className="text-gray-600 dark:text-neutral-400">
              Alle konsultasjoner slettes automatisk etter 24 timer. 
              Sørg for å overføre nødvendig informasjon til journal.
            </p>
          </div>
        </div>
      ) : (
        <div className="px-2 pb-4 pt-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-50 dark:bg-neutral-800 border border-amber-200 dark:border-neutral-700">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[220px]">
                <p className="text-sm font-medium mb-1">Automatisk sletting</p>
                <p className="text-xs">Konsultasjoner slettes automatisk etter 24 timer.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
    
    {/* Toggle Button for opening/closing sidebar */}

  </aside>
);
};

export default Sidebar;